import React, { useState, useEffect, useRef } from "react";
import CustomMultiSelectDropdown from "../multi-select-dropdown/multi-select-dropdown";
import NormalCustomDropdown from "../dropdown-main/DropdownMain";
import DatePickerWrapper from "../expandable-table/table-datepicker/datepicker-wrapper";
import CalendarIcon from "../../assets/icons/Icon-Calendar-Enabled-20px.svg";
import dayjs from "../../util/dayjs-config";
import { getFormattedDate } from "../../util/time-helpers";
import "./filter-dropdown-row.scss";

const FilterDropdownRow = ({
        showConjunctiveDropdown = false,
        disableConjunctiveDropdown = false,

        appliedFilter:{
            filterType,
            filterType2,
            unSelectedItems,
            unSelectedItems2,
            usesDatePicker,
            usesDatePicker2,
            placeholderText,
            placeholderText2,
            pluralizedName,
            inclusionSelection,
            filterSelection,
            filterSelection2,
            isSearchable: appliedFilterIsSearchable,
        },
        conjunctionSelection,

        updateFilter,
        updateFilter2,
        updateInclusion,
        removeFilter,
        updateAllConjunction,
        isFocused,
        dateFormatID = "en-US",
        customDropdownToggleSelector,
        customDropdownItemSelector,
        pageIdentifier,
    }) => {

    const [chosenDate, setChosenDate] = useState(undefined);
    // TAD-5842A — Controls the inline date-filter calendar. It replaces the old
    // react-bootstrap Dropdown.Menu (a Popper overlay) that floated over the
    // panel's "+ Add Filter" control and the table — differently at different
    // screen sizes as Popper flipped/shifted it. The calendar now renders inline
    // below the row so the panel grows to fit it and nothing overlaps.
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const dateToggleRef = useRef(null);
    const calendarRef = useRef(null);
    const toggleButtonRef = useRef(null);

    // TAD-5842A — Dismiss the open calendar the way the old overlay did, plus
    // keyboard parity. A mousedown anywhere other than the date toggle or the
    // calendar (another filter row, the include/exclude dropdown, outside the
    // panel) collapses it; Escape collapses it too and returns focus to the
    // field. The toggle handles its own open/close, so clicks on it are ignored
    // here.
    useEffect(() => {
        if (!isCalendarOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (dateToggleRef.current?.contains(event.target)) return;
            if (calendarRef.current?.contains(event.target)) return;
            setIsCalendarOpen(false);
        };

        const handleEscape = (event) => {
            if (event.key !== "Escape") return;
            setIsCalendarOpen(false);
            toggleButtonRef.current?.focus();
        };

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [isCalendarOpen]);

    const includeExcludeOptions = [{name: "Includes", id: 0}, {name: "Excludes", id: 1}];
    const conjunctionOptions = [{name: "And", id: 0}, {name: "Or", id: 1}];

    // Prefer the explicit `isSearchable` flag carried on each filter definition,
    // because labels overrides (e.g. ProjectSingular -> "Theme") rewrite `filterType`,
    // which would otherwise silently drop the search input. Fallback to the legacy
    // filterType-based heuristic for any callers that haven't been migrated yet.
    //
    // TODO: Every filter in INITIAL_PROJECT_FILTER_DROPDOWN_STATE and
    // INITIAL_TASK_FILTER_DROPDOWN_STATE now carries an explicit `isSearchable`
    // flag, so this fallback should be unreachable. Leaving it in place for one
    // release as a safety net for any out-of-tree callers / third-party filter
    // definitions; remove `SEARCHABLE_FILTER_TYPES` and inline `isFilterSearchable`
    // in a follow-up ticket once that's confirmed.
    const SEARCHABLE_FILTER_TYPES = ["Project", "Tags", "Member", "Project Name", "Task Name"];
    const isFilterSearchable = typeof appliedFilterIsSearchable === "boolean"
        ? appliedFilterIsSearchable
        : SEARCHABLE_FILTER_TYPES.includes(filterType);

    const formatDropdownBtn = (date) => {
        if (date) {
            return getFormattedDate(date);
        } else if (date == null && filterSelection[0]) {
            return (getFormattedDate(convertStringToDate(filterSelection)));
        } else {
            return "MM/DD/YYYY";
        }
    }

    const getformatDropdownText = (date) => {
        if (date) {
            return ('dropdown-button-text-format');
        } else if (date == null && filterSelection[0]) {
            return ('dropdown-button-text-format');
        } else {
            return ('dropdown-button-text-inactive');
        }
    }

    const convertStringToDate = (dateStringObjectsArray) => {
        if(!dateStringObjectsArray || dateStringObjectsArray.length <= 0) return undefined;

        const dateString = dateStringObjectsArray[0].name;
        const convertedDate = dayjs(dateString, 'YYYY-MM-DD');

        if(!convertedDate.isValid()) return undefined;

        return convertedDate.toDate();
    }

    return(
        <div className = {`filter-selection-container${isFocused ? " focused-filter": ""}`}>
            <div className = "dropdown-row-container">

                <div className = "left-dropdown">
                    {
                        showConjunctiveDropdown &&
                        <NormalCustomDropdown
                            disabled = {disableConjunctiveDropdown}
                            values = {conjunctionOptions}
                            onSelect={(updatedData) => updateAllConjunction(updatedData)}
                            currentSelected = {conjunctionSelection}
                        />

                    }
                </div>
                <div className = 'centre-container'>
                    <span className = 'filter-type-label'>{filterType}</span>
                    { usesDatePicker ?
                        <div className="dropdown-main-multiselect" ref={dateToggleRef}>
                            <div
                                id='dropdown-toggle'
                                className='dropdown-button'
                                role='button'
                                tabIndex={0}
                                aria-haspopup='dialog'
                                aria-expanded={isCalendarOpen}
                                ref={toggleButtonRef}
                                onClick={() => setIsCalendarOpen((open) => !open)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setIsCalendarOpen((open) => !open);
                                    }
                                }}
                            >
                                <span className={getformatDropdownText(chosenDate)}>{formatDropdownBtn(chosenDate)}</span>
                                {/* Decorative icon — empty alt + aria-hidden so the toggle's
                                    accessible name is just the field text, not "calendar-icon". */}
                                <img alt='' aria-hidden='true' src={CalendarIcon} style={{float: 'right', padding: '2px'}}/>
                            </div>
                        </div>
                    :
                        <span className="dropdowns-container">
                            <CustomMultiSelectDropdown
                                dropdownStyleClassName = {`primary-dropdown ${filterType != "Member" || pageIdentifier != "task" ?  "primary-dropdown-full-width" : ""}`}
                                CustomDropdownToggle={customDropdownToggleSelector(filterType,filterSelection)}
                                customDropdownRenderer={customDropdownItemSelector(filterType)}
                                placeholderText={placeholderText}
                                onSelect={(updatedSelectedItem, updatedUnselectedItems) => updateFilter(filterType,updatedSelectedItem,updatedUnselectedItems )}
                                unSelectedValues = {unSelectedItems}
                                selectedValues = {filterSelection}
                                pluralizedText={pluralizedName}
                                isSearchable={isFilterSearchable}
                                pageIdentifier={pageIdentifier}
                            />

                            {/* Sub-filter applied to members => but can be added to the others filters in the future just adding them here */}
                            { pageIdentifier == "task" && filterType == "Member" &&
                                <CustomMultiSelectDropdown
                                    dropdownStyleClassName = "secondary-dropdown"
                                    CustomDropdownToggle={customDropdownToggleSelector(filterType2,filterSelection2)}
                                    customDropdownRenderer={customDropdownItemSelector(filterType2)}
                                    placeholderText={placeholderText2}
                                    onSelect={(updatedSelectedItem2, updatedUnselectedItems2) =>
                                        updateFilter2(filterType2,updatedSelectedItem2,updatedUnselectedItems2)
                                    }
                                    unSelectedValues = {unSelectedItems2}
                                    selectedValues = {filterSelection2}
                                    pluralizedText={pluralizedName}
                                    isSearchable={isFilterSearchable}
                                    pageIdentifier={pageIdentifier}
                                />
                            }
                        </span>
                    }
                </div>
                <div className = "right-dropdown">
                    <NormalCustomDropdown
                        values = {includeExcludeOptions}
                        onSelect={(updatedData) => updateInclusion(filterType,updatedData)}
                        currentSelected = {inclusionSelection}
                    />
                    <button
                        className = 'clear-button'
                        onClick = {() => removeFilter(filterType)}>
                    </button>
                </div>
            </div>

            {/* TAD-5842A — The date-filter calendar expands inline here, as a
                sibling of the row (not inside .centre-container). Keeping it out
                of the row preserves the row's flex-end alignment of the
                include/exclude + clear controls; the panel simply grows to fit
                the calendar so it never overlaps the "+ Add Filter" link or the
                table at any screen size. */}
            { usesDatePicker && isCalendarOpen &&
                <div className="dropdown-main-calendar" ref={calendarRef}>
                    <DatePickerWrapper
                        placeholderText = {placeholderText}
                        selected = {convertStringToDate(filterSelection)}
                        dateFormatID ={dateFormatID}
                        onChange = {updatedDate => updateFilter(filterType, updatedDate, [])}
                        chosenDate = {chosenDate}
                        setChosenDate = {setChosenDate}
                        filterType={filterType}
                        removeFilter={updatedDate => updateFilter(filterType, undefined, [])}
                    />
                </div>
            }
        </div>
    )
}

export default FilterDropdownRow;
